import {
  Position,
  IPiece,
  MoveType,
  IMove,
  ISideEffect,
  EffectType,
  PieceType,
  Effect,
  Mode,
} from "../types";
// Force TypeScript recompile
import { GameStateManager } from "./GameStateManager";
import { animalPieceTypes } from "../const";
import { SideEffect } from "../SideEffect";
import { UUID } from "crypto";
import { Piece } from "../Piece";
import { Move } from "../Move";
import { ZodiacFEN } from "../Fen/ZodiacFEN";
import { GetSideEffectHandler } from "../PieceMovement/Handlers/SideEffect/SideEffectHandler";
import { GetMoveHandler } from "../PieceMovement/Handlers/Move/MoveHandler";
import { EventManager } from "./EventManager";
import { BonusTurnManager } from "./BonusTurnManager";
import { FENHistoryManager } from "./FENHistoryManager";
import { TurnController } from "./TurnController";

export class MovementManager {
  private readonly gameStateManager: GameStateManager;
  private readonly eventManager: EventManager | null;
  private readonly bonusTurnManager: BonusTurnManager | null; // Phase 4 - SOC refactoring
  private readonly fenHistoryManager: FENHistoryManager | null; // Phase 4 - SOC refactoring
  private readonly turnController: TurnController | null; // For zombie ink trap turn ending
  private verboseLogging: boolean = false;
  // Store FENs for the current turn (including bonus moves)
  private currentTurnFENs: string[] = [];
  // Store the last dice roll for FEN annotation
  private lastDiceRoll: number | null = null;
  // Store if the last leap was a swing (leaped over a Tree)
  private lastLeapWasSwing: boolean = false;
  // Track if we've already set shadows once (for one-turn shadow mechanic)
  private shadowSet: boolean = false;

  constructor(
    gameStateManager: GameStateManager,
    eventManager?: EventManager,
    bonusTurnManager?: BonusTurnManager,
    fenHistoryManager?: FENHistoryManager,
    turnController?: TurnController,
    private readonly rollDie: () => number = () => Math.floor(Math.random() * 6) + 1
  ) {
    this.gameStateManager = gameStateManager;
    this.eventManager = eventManager || null;
    this.bonusTurnManager = bonusTurnManager || null; // Phase 4 - SOC refactoring
    this.fenHistoryManager = fenHistoryManager || null; // Phase 4 - SOC refactoring
    this.turnController = turnController || null; // For zombie ink trap turn ending
  }

  /**
   * Get the game state manager
   * @returns The game state manager
   */
  getGameStateManager(): GameStateManager {
    return this.gameStateManager;
  }

  /**
   * Get the event manager
   * @returns The event manager (null if not provided in constructor)
   */
  getEventManager(): EventManager {
    if (!this.eventManager) {
      throw new Error("EventManager not available - MovementManager was created without EventManager for internal use");
    }
    return this.eventManager;
  }

  /**
   * Set whether to use verbose logging
   * @param isVerbose Whether to enable verbose logging
   */
  setVerboseLogging(isVerbose: boolean): void {
    this.verboseLogging = isVerbose;
  }

  /**
   * Set whether shadows are active (should be removed on next move)
   * Used by Firebase sync to restore shadow state from previous turn
   * @param isActive Whether shadows exist from a previous move
   */
  setShadowsActive(isActive: boolean): void {
    this.shadowSet = isActive;
  }

  makeMove(
    fromPosition: Position,
    toPosition: Position,
    piece: IPiece,
    toPiece: IPiece | null,
    moveType: MoveType,
    sideEffects: ISideEffect[]
  ): boolean {
    // Execution must enforce the same lock as move generation. A stale AI/UI
    // action must never spend a Virus continuation on another piece.
    const activeBonusPiece = this.gameStateManager.getGameState().activeBonusTurnPieceId;
    if (activeBonusPiece && activeBonusPiece !== piece.id) return false;
    // Only remove shadows after the first move that could have created them
    // ZOMBIE INK: Don't auto-remove if zombie ink shadows exist for current player
    // Those shadows persist until the player interacts with them (picks real or shadow octopus)
    let shouldRemoveShadows = false;
    if (this.shadowSet) {
      // Check if zombie ink shadows exist for current player - if so, don't auto-remove
      if (this.hasZombieInkShadowsForCurrentTurn()) {
        // Zombie ink shadows - don't remove, let the player deal with them
        shouldRemoveShadows = false;
      } else {
        shouldRemoveShadows = true;
      }
    } else {
      this.shadowSet = true;
    }

    if (this.verboseLogging) {
    }

    let validMoves = piece.getValidMovesForPiece(
      this.gameStateManager.getBoardState(),
      fromPosition,
      this.gameStateManager.getGameState().isLightTurn,
      this.gameStateManager
    );

    // Store which side is moving at the start
    const wasLightTurn = this.gameStateManager.getGameState().isLightTurn;

    // Block illegal Virus endTurn on regular turns (only allowed during bonus turns)
    if (moveType === MoveType.endTurn && piece.type === PieceType.Virus) {
      const gameState = this.gameStateManager.getGameState();
      if (gameState.activeBonusTurnPieceId !== piece.id) {
        return false;
      }
    }

    if (
      validMoves.some(
        (x) =>
          x.position.col === toPosition.col && x.position.row === toPosition.row
      )
    ) {
      // Wake up fainted goats whenever they move, regardless of move type
      if (piece.type === PieceType.Goat && piece.isFainted) {
        piece.isFainted = false;
      }

      piece.hasMoved = true;
      if (piece.type === PieceType.Frog) {
        piece.hasLilyJumped = true;
      }

      // Check if an octopus is making a non-entangle move to release its entangled piece
      if (
        piece.type === PieceType.Octopus &&
        piece.entangledPieceId &&
        !sideEffects.some(
          (effect) => effect.effect.type === EffectType.entangle
        )
      ) {
        const entangledPiece = this.gameStateManager.findPieceById(
          piece.entangledPieceId
        );
        if (entangledPiece) {
          entangledPiece.isEntangled = false;
          piece.entangledPieceId = undefined;
        }
      }

      // NEW move handling
      const migratedMoves: MoveType[] = [MoveType.ink, MoveType.swing, MoveType.consume];
      if (migratedMoves.includes(moveType)) {
        // CRITICAL: Capture bonus turn state BEFORE move handler runs
        // This lets us distinguish between:
        // - A move that GRANTS a bonus turn (wasInBonusTurn = false)
        // - A move DURING a bonus turn (wasInBonusTurn = true)
        const gameStateBefore = this.gameStateManager.getGameState();
        const wasInBonusTurnBeforeMove =
          gameStateBefore.activeBonusTurnPieceId === piece.id &&
          gameStateBefore.skipTurnChange;


        const handler = GetMoveHandler(moveType);
        // Clear the previous ink before marking the new real Octopus shadowed.
        if (shouldRemoveShadows) this.removeAllShadowPieces();
        handler.handleMove({
          movementManager: this,
          fromPosition: fromPosition,
          toPosition: toPosition,
          piece: piece,
          toPiece: toPiece,
          sideEffects: sideEffects,
        });


        // Remove old shadow pieces before creating new ones
        // Fixes Sandbox bug where consecutive inks accumulate shadows
        if (piece.isInfected) this.moveVirusWithInfectedPiece(piece, fromPosition);

        // Process side effects for migrated moves (e.g. ink)
        this.handleSideEffects(
          fromPosition,
          toPosition,
          piece,
          toPiece,
          moveType,
          sideEffects
        );

        // After every move (including bonus moves), store the FEN
        const gameState = this.gameStateManager.getGameState();
        let fen = ZodiacFEN.encodeWithoutKings(gameState);

        // Add "-" prefix for bonus turn moves (moves made DURING a bonus turn)
        if (wasInBonusTurnBeforeMove) {
          fen = "-" + fen;
        }

        if (this.lastDiceRoll !== null) {
          fen += `#${this.lastDiceRoll}`;
          this.lastDiceRoll = null;
        }
        // BUG FIX: Add turn count metadata so UI can group bonus turn moves together
        // All moves with the same @turnCount should display as a single turn in move history
        // This fixes the turn counter explosion issue where Virus bonus turns showed as 40+ separate moves
        // CRITICAL: ALWAYS use turnCount + 1 (even for bonus turns)
        // When first move happens, turnCount=0 but this is turn 1 for display purposes
        // Bonus turns should have the SAME turn number as their parent move
        const displayTurnNumber = gameState.turnCount + 1;
        fen += `@${displayTurnNumber}`;
        this.currentTurnFENs.push(fen);

        // BONUS TURN COMPLETION: If this move was made DURING an existing bonus turn,
        // handle bonus turn continuation or ending
        // NOTE: Only check if we were ALREADY in bonus turn BEFORE the move
        if (wasInBonusTurnBeforeMove) {
          let shouldEndBonusTurn = false;
          
          if (piece.type === PieceType.Virus) {
            // For virus: end bonus turn if no more infection moves OR if this was an endTurn move
            if (moveType === MoveType.endTurn) {
              shouldEndBonusTurn = true;
            } else if (moveType === MoveType.infect) {
              // Check if virus has any remaining infection moves after this move
              const currentPosition = this.gameStateManager.findPiecePosition(piece);
              if (currentPosition) {
                const validMoves = piece.getValidMovesForPiece(
                  this.gameStateManager.getBoardState(),
                  currentPosition,
                  this.gameStateManager.getGameState().isLightTurn,
                  this.gameStateManager
                );
                const remainingInfectMoves = validMoves.filter(m => m.moveType === MoveType.infect);
                
                if (remainingInfectMoves.length === 0) {
                  shouldEndBonusTurn = true;
                } else {
                }
              }
            }
          } else if (piece.type === PieceType.Monkey) {
            // For monkey in NEW move handling: only swing and consume reach this section
            // Both swing and consume grant new bonus turns (monkey can't do ink moves)
            // So never end the bonus turn for monkey moves in this section - they all chain
            shouldEndBonusTurn = false;
          } else {
            // For other pieces: end bonus turn after move
            shouldEndBonusTurn = true;
          }

          if (shouldEndBonusTurn) {

            // Process any pending zombification from enemy fungi consumption
            const pendingZombification = (gameState as any).pendingZombification;
            if (pendingZombification && pendingZombification.pieceId === piece.id) {
              piece.zombifiedStatus = {
                isZombified: true,
                removeOnTurn: gameState.turnCount + 3 // Lasts 3 turns
              };
              piece.isZombified = true;
              (gameState as any).pendingZombification = null; // Clear the pending state
            }

            this.gameStateManager.clearBonusTurn();
          } else {
          }
        }

        return true;
      }

      // OLD move handling
      // @llm this is the old way of handling moves, please use the new way above

      // CRITICAL: Capture bonus turn state BEFORE move execution (same as new move handling)
      const gameStateBefore = this.gameStateManager.getGameState();
      const wasInBonusTurnBeforeMove =
        gameStateBefore.activeBonusTurnPieceId === piece.id &&
        gameStateBefore.skipTurnChange;

      switch (moveType) {
        case MoveType.noMove:
        case MoveType.rotate:
          // nothing to do here for movement! (but side effects will be processed)
          break;
        case MoveType.regular:
          this.regularMove(fromPosition, toPosition, piece);
          break;
        case MoveType.traverse:
          this.traverseMove(fromPosition, toPosition, piece);
          break;
        case MoveType.faint:
          this.faintMove(fromPosition, toPosition, piece);
          break;
        case MoveType.capture:
          this.captureMove(
            fromPosition,
            toPosition,
            piece,
            toPiece!,
            sideEffects
          );
          break;
        // case MoveType.consume:
        // Consume moves are now handled by the new handler system above
        case MoveType.house:
          this.houseMove(fromPosition, toPosition, piece, toPiece!);
          break;
        case MoveType.lane:
          this.laneMove(fromPosition, toPosition, piece, toPiece!);
          break;
        case MoveType.trample:
          this.trampleMove(
            fromPosition,
            toPosition,
            piece,
            toPiece!,
            sideEffects
          );
          break;
        case MoveType.leap:
          this.leapMove(fromPosition, toPosition, piece, toPiece, sideEffects);
          break;
        case MoveType.infect:
          this.infectMove(
            fromPosition,
            toPosition,
            piece,
            toPiece!,
            sideEffects
          );
          break;
        case MoveType.endTurn:
          // For endTurn, do a simple move back to the same position (no move essentially)
          // This is just to process the move but not actually move anything
          this.endTurnMove(fromPosition, toPosition, piece);
          // Clear bonus turn state if this was a bonus turn piece
          if (
            this.gameStateManager.getGameState().activeBonusTurnPieceId ===
            piece.id
          ) {
            this.gameStateManager.clearBonusTurn();
          }
          break;

        //case MoveType.ink:
        // Legacy inkMove is now handled exclusively by the new handler system above.
        // See ARCHITECTURE.md: Separation of Concerns, Single Responsibility, and migration to handler-based move processing.
        // break;

        default:
          throw new Error(`Unhandled move type: ${moveType}`);
      }

      if (piece.isInfected) {
        this.moveVirusWithInfectedPiece(piece, fromPosition);
      }

      this.handleSideEffects(
        fromPosition,
        toPosition,
        piece,
        toPiece,
        moveType,
        sideEffects
      );

      // MONKEY-CONSUME-FIX: Apply pending monkey zombification BEFORE FEN encoding
      // This ensures the bonus move's FEN correctly shows the monkey as zombified (Mz)
      // Only applies to the bonus move AFTER consuming enemy fungi (not the consume move itself)
      const gameState = this.gameStateManager.getGameState();
      if (wasInBonusTurnBeforeMove && piece.type === PieceType.Monkey) {
        const pendingMonkeyZombi = (gameState as any).pendingMonkeyZombification;
        if (pendingMonkeyZombi && pendingMonkeyZombi.pieceId === piece.id) {
          // removeOnTurn = turnCount + 2 because after this:
          // 1. checkGameEnd() → changeTurns() → sandbox handleSandboxMode increments turnCount (+1)
          // 2. runPostTurn() clears zombie if removeOnTurn === turnCount (would clear at +1)
          // So +2 ensures zombie survives this turn cycle and clears on the NEXT move
          piece.zombifiedStatus = {
            isZombified: true,
            removeOnTurn: gameState.turnCount + 2
          };
          piece.isZombified = true;
          (gameState as any).pendingMonkeyZombification = null;
        }
      }

      // MONKEY-CONSUME-FIX: Clear expired monkey zombification BEFORE FEN encoding
      // Normal clearing happens in changeTurns/runPostTurn AFTER FEN is captured,
      // so without this check the zombie would show for 1 extra move in the FEN
      if (piece.type === PieceType.Monkey && piece.isZombified &&
          piece.zombifiedStatus.removeOnTurn !== undefined &&
          piece.zombifiedStatus.removeOnTurn <= gameState.turnCount) {
        piece.zombifiedStatus = { isZombified: false, removeOnTurn: undefined };
        piece.isZombified = false;
      }

      // After every move (including bonus moves), store the FEN
      let fen = ZodiacFEN.encodeWithoutKings(gameState);

      // Add "-" prefix for bonus turn moves (moves made DURING a bonus turn)
      if (wasInBonusTurnBeforeMove) {
        fen = "-" + fen;
      }

      if (this.lastDiceRoll !== null) {
        fen += `#${this.lastDiceRoll}`;
        this.lastDiceRoll = null;
      }
      // BUG FIX: Add turn count metadata so UI can group bonus turn moves together
      // CRITICAL: ALWAYS use turnCount + 1 (even for bonus turns)
      // Bonus turns should have the SAME turn number as their parent move
      const displayTurnNumber = gameState.turnCount + 1;
      fen += `@${displayTurnNumber}`;
      this.currentTurnFENs.push(fen);

      // BONUS TURN COMPLETION: If this move was made during a bonus turn by the bonus turn piece,
      // handle bonus turn continuation or ending
      // CRITICAL: Use wasInBonusTurnBeforeMove captured BEFORE the move, not current state
      // We can't rely on piece.id === activeBonusTurnPieceId due to Firebase sync changing IDs
      if (wasInBonusTurnBeforeMove) {
        // This move was made DURING an active bonus turn

        let shouldEndBonusTurn = false;

        if (piece.type === PieceType.Virus) {
          // For virus: end bonus turn if no more infection moves OR if this was an endTurn move
          if (moveType === MoveType.endTurn) {
            shouldEndBonusTurn = true;
          } else if (moveType === MoveType.infect) {
            // Check if virus has any remaining infection moves after this move
            const currentPosition = this.gameStateManager.findPiecePosition(piece);
            if (currentPosition) {
              const validMoves = piece.getValidMovesForPiece(
                this.gameStateManager.getBoardState(),
                currentPosition,
                this.gameStateManager.getGameState().isLightTurn,
                this.gameStateManager
              );
              const remainingInfectMoves = validMoves.filter(m => m.moveType === MoveType.infect);
              
              if (remainingInfectMoves.length === 0) {
                shouldEndBonusTurn = true;
              } else {
              }
            }
          }
        } else if (piece.type === PieceType.Penguin) {
          const isZombified = piece.zombifiedStatus?.isZombified || false;

          // ZOMBIFIED PENGUIN: Reverse bonus turn logic
          // Normal: slide/capture → rotation (ends turn)
          // Zombified: rotation → slide/capture (ends turn + removes zombification)
          if (isZombified) {
            // Zombified penguin: slide/capture during bonus turn ends turn + marks zombification for removal
            if (moveType === MoveType.regular || moveType === MoveType.capture) {
              shouldEndBonusTurn = true;
              // IMPORTANT: Mark for removal at current turn end (not +1, since bonus turn doesn't increment turnCount)
              // The turn count protection means currentTurnCount stays the same during bonus turn
              const currentTurnCount = this.gameStateManager.getGameState().turnCount;
              piece.zombifiedStatus = {
                isZombified: true,  // Keep zombified through bonus turn!
                removeOnTurn: currentTurnCount  // Remove when THIS turn ends (after bonus turn completes)
              };
              piece.isZombified = true;
            } else if (moveType === MoveType.endTurn) {
              shouldEndBonusTurn = true;
            } else if (moveType === MoveType.rotate) {
              // Rotation should grant bonus turn (already handled by side effect)
            }
          } else {
            // NORMAL PENGUIN: Standard bonus turn logic
            // For penguin: the move that granted the bonus turn should not end it immediately
            // Only rotation moves or explicit end turn moves should end the bonus turn
            if (moveType === MoveType.endTurn) {
              shouldEndBonusTurn = true;
            } else if (moveType === MoveType.rotate) {
              // MoveType.rotate = rotation move = should end bonus turn
              shouldEndBonusTurn = true;
            } else if (moveType === MoveType.noMove) {
              // noMove with rotate side effect = rotation move = should end bonus turn
              const hasRotateSideEffect = sideEffects.some(se => se.effect.type === EffectType.rotate);
              if (hasRotateSideEffect) {
                shouldEndBonusTurn = true;
              } else {
              }
            } else {
              // For regular/capture moves that just granted the bonus turn, continue the bonus turn
            }
          }
        } else if (piece.type === PieceType.Monkey) {
          // For monkey in OLD move handling: swing and consume are handled in NEW section above
          // EXCEPTION: Leap-consume (leap landing on Fungi) should grant a new bonus turn
          // The BonusTurn side effect was added by MonkeyMovement and will be processed by BonusTurnHandler
          if (moveType === MoveType.leap && toPiece?.type === PieceType.Fungi) {
            // Leap-consume grants new bonus turn - don't end the current one
            shouldEndBonusTurn = false;
          } else {
            // For other OLD section moves, end the bonus turn
            shouldEndBonusTurn = true;
          }
        } else {
          // For other pieces: old system moves don't grant bonus turns anymore (except virus and penguin)
          shouldEndBonusTurn = true;
        }

        if (shouldEndBonusTurn) {

          // Process any pending zombification from enemy fungi consumption
          const pendingZombification = (gameState as any).pendingZombification;
          if (pendingZombification && pendingZombification.pieceId === piece.id) {
            piece.zombifiedStatus = {
              isZombified: true,
              removeOnTurn: gameState.turnCount + 3 // Lasts 3 turns
            };
            piece.isZombified = true;
            (gameState as any).pendingZombification = null; // Clear the pending state
          }

          this.gameStateManager.clearBonusTurn();
        } else {
        }
      }

      // ZOMBIE PENGUIN FIRST ROTATION: Check if this rotation just granted a bonus turn
      // This handles the case where zombie penguin rotates as first move (not during bonus turn)
      // Zombie penguins use REVERSE order: rotate first (grants bonus) -> slide/capture (ends turn)
      if (!wasInBonusTurnBeforeMove &&
          piece.type === PieceType.Penguin &&
          piece.zombifiedStatus?.isZombified &&
          moveType === MoveType.rotate) {
        // Verify bonus turn was granted by side effect
        const bonusTurnGranted = this.gameStateManager.getGameState().activeBonusTurnPieceId === piece.id;
        if (bonusTurnGranted) {
        } else {
          // If bonus turn wasn't granted, something went wrong - grant it now as fallback
          this.gameStateManager.grantBonusTurn(piece);
        }
      }

      // Remove all shadow pieces (one-turn diversion mechanic) at the end of the move
      if (shouldRemoveShadows) {
        this.removeAllShadowPieces();
      }

      // Validate meadow effects - remove meadow from trees that no longer have adjacent enemy fungi
      this.validateMeadowEffects();

      // You might need to send updates or notify listeners here if necessary

      // === Expire poison/zombification at the very end of the move ===
      // this.removeEndofturn();
      // === End expire logic ===

      return true;
    } else {
      return false;
    }
  }

  handleSideEffects(
    fromPosition: Position,
    toPosition: Position,
    piece: IPiece,
    toPiece: IPiece | null,
    moveType: MoveType,
    sideEffects: ISideEffect[]
  ): void {
    if (sideEffects.length === 0) return;

    // new side effect handlers
    if (piece.type === PieceType.Octopus) {
      this.handleEntangleSideEffects(piece as Piece, sideEffects);
    }

    // NEW side effect handling
    // get distinct side effect types, for each type get a handler and call it
    var migratedEffectTypes = [
      EffectType.bonusTurn,
      EffectType.entangle,
      EffectType.poison,
      EffectType.shadow,
      EffectType.zombify,
      EffectType.rotate,
    ];

    // Process side effects through their appropriate handlers
    [...new Set(sideEffects.map((x) => x.effect.type))]
      .filter((effectType) => migratedEffectTypes.includes(effectType))
      .forEach((effectType) => {
        const handler = GetSideEffectHandler(effectType);

        const moveInfoParam = {
          moveType: moveType,
          fromPosition: fromPosition,
          toPosition: toPosition,
        };

        // Filter just the effects of this type
        const relevantEffects = sideEffects.filter(
          (effect) => effect.effect.type === effectType
        );

        handler.handleSideEffect({
          inflictingPiece: piece,
          sideEffects: relevantEffects,
          currentTurnNumber: this.gameStateManager.getGameState().turnCount,
          movementManager: this,
          moveInfo: moveInfoParam,
        });
      });

    // OLD Side effect handling
    sideEffects.forEach((sideEffect) => {
      if (sideEffect.effect.type === EffectType.capture) {
        sideEffect.piecesToEffect.forEach((capturedPiece) => {
          // Release any entangled pieces if an octopus is captured
          if (
            capturedPiece.type === PieceType.Octopus &&
            capturedPiece.entangledPieceId
          ) {
            const entangledPiece = this.gameStateManager.findPieceById(
              capturedPiece.entangledPieceId
            );
            if (entangledPiece) {
              entangledPiece.isEntangled = false;
            }
          }

          // VIRUS TRANSFER: If the captured piece is infected, transfer the virus to the capturing piece
          if (capturedPiece.isInfected) {
            const captureSquare = this.gameStateManager.getSquare(sideEffect.position);
            const virusPiece = captureSquare.Pieces.find(p => p.type === PieceType.Virus);

            if (virusPiece) {
              // Mark the capturing piece as infected
              piece.isInfected = true;

              // Find the capturing piece's position (should be toPosition after the move)
              const capturingPiecePosition = this.gameStateManager.findPiecePosition(piece);

              if (capturingPiecePosition) {
                // Remove virus from the capture square
                const virusIndex = captureSquare.Pieces.findIndex(p => p.type === PieceType.Virus);
                if (virusIndex !== -1) {
                  captureSquare.Pieces.splice(virusIndex, 1);
                }

                // Add virus to the capturing piece's square
                const capturingSquare = this.gameStateManager.getSquare(capturingPiecePosition);
                capturingSquare.Pieces.push(virusPiece);
              }
            }
          }

          // Remove the captured piece from the board
          this.gameStateManager.removePiece(sideEffect.position, capturedPiece);
        });
      }

      // Meadow logic
      if (sideEffect.effect.type === EffectType.meadow) {
        sideEffect.piecesToEffect.forEach((adjacentPiece) => {
          if (
            adjacentPiece.type === PieceType.Tree &&
            adjacentPiece.isLight !== piece.isLight
          ) {
            adjacentPiece.isMeadowed = true;
          }
        });
      }

      // House move logic
      if (sideEffect.effect.type === EffectType.housemove) {
        sideEffect.piecesToEffect.forEach((sidePiece) => {
          if (sidePiece.type !== PieceType.Tree) {
            this.regularMove(fromPosition, sideEffect.position, sidePiece);
          }
        });
      }
    });
  }

  private moveVirusWithInfectedPiece(
    piece: IPiece,
    fromPosition: Position
  ): void {
    // Find the piece's current position after the move
    const currentPosition = this.gameStateManager.findPiecePosition(piece);

    if (!currentPosition) {
      return;
    }

    // Move the virus from fromPosition to currentPosition
    const fromSquare = this.gameStateManager.getSquare(fromPosition);
    const toSquare = this.gameStateManager.getSquare(currentPosition);

    // Find the virus piece on the fromSquare
    const virusPieceIndex = fromSquare.Pieces.findIndex(
      (p) => p.type === PieceType.Virus
    );

    if (virusPieceIndex !== -1) {
      const virusPiece = fromSquare.Pieces[virusPieceIndex];
      // Remove virus piece from fromSquare
      fromSquare.Pieces.splice(virusPieceIndex, 1);
      // Add virus piece to toSquare
      toSquare.Pieces.push(virusPiece);
    }
  }

  leapMove(
    fromPosition: Position,
    toPosition: Position,
    piece: IPiece,
    toPiece: IPiece | null,
    sideEffects: ISideEffect[]
  ): void {
    // 1. Figure out the step increments (just like `trampleMove`)
    const rowDiff = toPosition.row - fromPosition.row;
    const colDiff = toPosition.col - fromPosition.col;
    const rowStep = rowDiff === 0 ? 0 : rowDiff > 0 ? 1 : -1;
    const colStep = colDiff === 0 ? 0 : colDiff > 0 ? 1 : -1;

    // 2. Collect all squares along the path
    //    (EXCLUDING the fromPosition, up to but NOT including toPosition)
    let currentRow = fromPosition.row + rowStep;
    let currentCol = fromPosition.col + colStep;

    // Monkey shadow-fall logic: if a monkey leaps over a shadow, it falls on the first shadow square
    if (piece.type === PieceType.Monkey) {
      let fallRow = null;
      let fallCol = null;
      let foundShadow = false;
      let checkRow = fromPosition.row + rowStep;
      let checkCol = fromPosition.col + colStep;
      while (checkRow !== toPosition.row || checkCol !== toPosition.col) {
        const currentPosition = { row: checkRow, col: checkCol };
        const square = this.gameStateManager.getSquare(currentPosition);
        if (square.Pieces.some((p) => p.isShadow)) {
          fallRow = checkRow;
          fallCol = checkCol;
          foundShadow = true;
          break;
        }
        checkRow += rowStep;
        checkCol += colStep;
      }
      if (fallRow !== null && fallCol !== null) {
        const fallSquare = this.gameStateManager.getSquare({
          row: fallRow,
          col: fallCol,
        });
        // If the shadow is gone (square is empty), still fall there
        if (foundShadow || fallSquare.Pieces.length === 0) {
          this.regularMove(fromPosition, { row: fallRow, col: fallCol }, piece);
          return;
        }
      }
      // Reset for normal leap if no shadow found
      currentRow = fromPosition.row + rowStep;
      currentCol = fromPosition.col + colStep;
    }

    let dangerCount = 0;
    let lastLeapedOverPiece: IPiece | null = null;
    let leapedOverTree = false;
    const jumpedEnemyPositions: Position[] = []; // Track enemy positions for monkey gamble

    while (currentRow !== toPosition.row || currentCol !== toPosition.col) {
      const currentPosition = { row: currentRow, col: currentCol };
      const square = this.gameStateManager.getSquare(currentPosition);

      // Store the last piece we leap over (if any)
      if (square.Pieces.length > 0) {
        lastLeapedOverPiece = square.Pieces[0];
        // Check if any leaped-over piece is a Tree
        if (square.Pieces.some((p) => p.type === PieceType.Tree)) {
          leapedOverTree = true;
        }
      }

      // Count all enemy pieces in this intermediate square
      const enemyPieces = square.Pieces.filter(
        (p) => p.isLight !== piece.isLight
      );
      dangerCount += enemyPieces.length;
      
      // For monkey gamble failure detection: track positions where enemies were jumped over
      if (enemyPieces.length > 0 && piece.type === PieceType.Monkey) {
        jumpedEnemyPositions.push({ row: currentRow, col: currentCol });
      }

      // Move to next square
      currentRow += rowStep;
      currentCol += colStep;
    }

    this.lastLeapWasSwing = leapedOverTree;

    // Bear capture is handled separately in captureMove(), not in leapMove()

    if (dangerCount > 0) {
      // In TestAI mode, use deterministic dice roll (always 6 = success) for reproducible tests
      const isTestMode = this.gameStateManager.getGameState().mode === Mode.TestAI;
      const roll = isTestMode ? 6 : this.rollDie();
      this.lastDiceRoll = roll;
      if (isTestMode) {
      }
    } else {
      this.lastDiceRoll = null;
    }

    // 4. Compare roll vs. danger
    if (
      dangerCount > 0 &&
      this.lastDiceRoll !== null &&
      this.lastDiceRoll <= dangerCount
    ) {
      // Monkey fails the leap: set failure info for loss detection and remove monkey
      if (piece.type === PieceType.Monkey) {
        this.gameStateManager.setMonkeyGambleFailure({
          diceRoll: this.lastDiceRoll,
          dangerCount: dangerCount,
          jumpedEnemies: jumpedEnemyPositions,
          bearCapturePosition: undefined // Bear capture handled separately in captureMove()
        });
      }
      
      this.gameStateManager.removePiece(fromPosition, piece);
      return;
    } else {
      // Monkey succeeds the leap - clear any previous failure
      if (piece.type === PieceType.Monkey) {
        this.gameStateManager.setMonkeyGambleFailure(undefined);
      }

      // CRITICAL: Clear swing return block for monkey leap moves during bonus turn
      if (piece.type === PieceType.Monkey && this.gameStateManager.getGameState().activeBonusTurnPieceId) {
        this.gameStateManager.clearSwingReturnBlock();
      }

      // If there's a piece on the final square, capture it
      if (toPiece) {
        this.captureMove(fromPosition, toPosition, piece, toPiece, sideEffects);

        // FIX: Handle zombification when monkey leap-consumes enemy fungi
        // This matches the behavior in ConsumeMoveHandler for regular consume moves
        if (piece.type === PieceType.Monkey && toPiece.type === PieceType.Fungi) {
          // Check if it's enemy fungi (schedule zombification after bonus turn)
          if (toPiece.isLight !== piece.isLight) {
            const gameState = this.gameStateManager.getGameState();
            (gameState as any).pendingZombification = {
              pieceId: piece.id,
              reason: 'consume_enemy_fungi'
            };
          }
        }
      } else {
        // Otherwise, just do a normal move
        this.regularMove(fromPosition, toPosition, piece);
      }

    }
  }

  private faintMove(
    fromPosition: Position,
    toPosition: Position,
    piece: IPiece
  ): void {
    piece.isFainted = true;
  }

  private traverseMove(
    fromPosition: Position,
    toPosition: Position,
    piece: IPiece
  ): void {
    // CRITICAL: Clear swing return block for monkey traverse moves during bonus turn
    if (piece.type === PieceType.Monkey && this.gameStateManager.getGameState().activeBonusTurnPieceId) {
      this.gameStateManager.clearSwingReturnBlock();
    }

    // Remove the piece from the fromPosition
    this.gameStateManager.removePiece(fromPosition, piece);
    // Add the piece to the toPosition
    this.gameStateManager.addPiece(toPosition, piece);
  }

  private infectMove(
    fromPosition: Position,
    toPosition: Position,
    piece: IPiece,
    toPiece: IPiece,
    sideEffects: ISideEffect[]
  ): void {
    this.regularMove(fromPosition, toPosition, piece);
    toPiece.isInfected = true;

    const fromSquare = this.gameStateManager.getSquare(fromPosition);
    const piecesToRemove: IPiece[] = [];

    if (fromSquare.Pieces.length === 1) {
      fromSquare.Pieces.forEach((squarePiece) => {
        if (animalPieceTypes.includes(squarePiece.type)) {
          piecesToRemove.push(squarePiece);
        }
      });
    }

    if (piecesToRemove.length === 1) {
      const capturedPiece = piecesToRemove[0];

      // SNAKE SHED: Check if captured piece is a Snake with shed ability
      if (capturedPiece.type === PieceType.Snake && !capturedPiece.hasShed) {
        this.snakeLivesMatter(capturedPiece, fromPosition, sideEffects);
      } else {
        // Normal capture - just remove the piece
        this.gameStateManager.removePiece(fromPosition, capturedPiece);
      }
    }
  }

  private simpleEntangleMove(
    fromPosition: Position,
    toPosition: Position,
    piece: IPiece,
    toPiece: IPiece,
    sideEffects: ISideEffect[]
  ): void {
    // Apply entangle logic
    if (
      piece.type === PieceType.Octopus &&
      toPiece.isLight !== piece.isLight &&
      animalPieceTypes.includes(toPiece.type)
    ) {
      toPiece.isEntangled = true;
    }
  }

  regularMove(fromPosition: Position, toPosition: Position, piece: IPiece) {
    // Check if the piece is a fainted goat - if so, wake it up
    if (piece.type === PieceType.Goat && piece.isFainted) {
      piece.isFainted = false; // Wake up the goat when it moves
    }

    // CRITICAL: Clear swing return block for monkey regular moves during bonus turn
    if (piece.type === PieceType.Monkey && this.gameStateManager.getGameState().activeBonusTurnPieceId) {
      this.gameStateManager.clearSwingReturnBlock();
    }

    // Debug: Log the move and what's at the destination before moving
    const toSquare = this.gameStateManager.getSquare(toPosition);
    if (toSquare.Pieces.length > 0) {
    }

    // Remove the piece from the fromPosition
    this.gameStateManager.removePiece(fromPosition, piece);
    // Add the piece to the toPosition
    this.gameStateManager.addPiece(toPosition, piece);
  }

  private laneMove(
    fromPosition: Position,
    toPosition: Position,
    piece: IPiece,
    toPiece: IPiece
  ): void {
    // Swap positions of the two pieces
    this.regularMove(fromPosition, toPosition, piece);
    this.regularMove(toPosition, fromPosition, toPiece);
  }

  private trampleMove(
    fromPosition: Position,
    toPosition: Position,
    piece: IPiece,
    toPiece: IPiece | null,
    sideEffects: ISideEffect[]
  ): void {
    const rowDiff = toPosition.row - fromPosition.row;
    const colDiff = toPosition.col - fromPosition.col;
    const rowStep = rowDiff === 0 ? 0 : rowDiff > 0 ? 1 : -1;
    const colStep = colDiff === 0 ? 0 : colDiff > 0 ? 1 : -1;

    let currentRow = fromPosition.row + rowStep;
    let currentCol = fromPosition.col + colStep;
    let trampledFrog = false;

    // Traverse the path to the destination
    while (currentRow !== toPosition.row || currentCol !== toPosition.col) {
      const currentPosition = { row: currentRow, col: currentCol };
      const square = this.gameStateManager.getSquare(currentPosition);
      const piecesAtPosition = square.Pieces.slice(); // Copy to avoid mutation issues

      if (piecesAtPosition.length > 0) {
        // Check if any trampled piece is a Frog (triggers poison)
        if (piecesAtPosition.some(p => p.type === PieceType.Frog)) {
          trampledFrog = true;
        }

        // Collect capture side effects
        sideEffects.push(
          new SideEffect(
            crypto.randomUUID() as UUID,
            currentPosition,
            piecesAtPosition,
            Effect.Capture()
          )
        );
      }

      currentRow += rowStep;
      currentCol += colStep;
    }

    // Check if destination piece is a Frog (also triggers poison)
    if (toPiece && toPiece.type === PieceType.Frog) {
      trampledFrog = true;
    }

    // Add poison side effect if any Frog was trampled
    if (trampledFrog) {
      sideEffects.push(
        new SideEffect(
          crypto.randomUUID() as UUID,
          toPosition, // Position where Rhino ends up
          [piece], // The Rhino gets poisoned
          Effect.Poison()
        )
      );
    }

    // Handle the final position (toPosition)
    if (toPiece) {
      this.captureMove(fromPosition, toPosition, piece, toPiece, sideEffects);
    } else {
      this.regularMove(fromPosition, toPosition, piece);
    }

    // Mega Charge logic
    const isMegaCharge = Math.abs(rowDiff) > 2 || Math.abs(colDiff) > 2;
    if (isMegaCharge) {
      piece.hasCharged = true;
    }
  }

  captureMove(
    fromPosition: Position,
    toPosition: Position,
    piece: IPiece,
    toPiece: IPiece,
    sideEffects: ISideEffect[]
  ) {
    // If capturing ANY Fungi (king or not), add Zombify side effect BEFORE side effect processing
    // The capturing piece becomes zombified and switches teams for one turn
    // EXCEPTION: Monkeys handle zombification specially via ConsumeMoveHandler/leapMove pendingZombification
    // to defer it until after the bonus turn completes
    if (
      toPiece.type === PieceType.Fungi &&
      animalPieceTypes.includes(piece.type) &&
      piece.isLight !== toPiece.isLight &&
      piece.type !== PieceType.Monkey // Monkey zombification is handled separately (deferred after bonus turn)
    ) {
      sideEffects.push(
        new SideEffect(
          crypto.randomUUID() as UUID,
          toPosition,
          [piece],
          Effect.Zombify()
        )
      );
    }
    // 1. Check if the attacking piece is a Monkey and the defender is a Bear of the opposite team.
    if (
      piece.type === PieceType.Monkey &&
      toPiece.type === PieceType.Bear &&
      piece.isLight !== toPiece.isLight
    ) {
      // 2. Perform dice roll.
      const roll = this.rollDie();
      this.lastDiceRoll = roll;
      if (roll <= 4) {
        // 3a. Monkey fails and gets captured. Remove Monkey from the board.
        // For monkey gamble tutorial: set failure info for loss effect positioning
        if (piece.type === PieceType.Monkey) {
          this.gameStateManager.setMonkeyGambleFailure({
            diceRoll: roll,
            dangerCount: 1, // Bear combat is 1 danger
            jumpedEnemies: [],
            bearCapturePosition: toPosition
          });
        }
        
        this.gameStateManager.removePiece(fromPosition, piece);
        return; // <- Stop here. The Monkey never moves onto the Bear's square.
      } else {
        // 3b. Monkey succeeds and captures Bear. Remove Bear, then move Monkey.
        // Clear any previous monkey gamble failure on successful bear capture
        if (piece.type === PieceType.Monkey) {
          this.gameStateManager.setMonkeyGambleFailure(undefined);
        }
        
        this.gameStateManager.removePiece(toPosition, toPiece);
        this.regularMove(fromPosition, toPosition, piece);
        return; // Then skip pushing any normal capture side effect if you want.
      }
    }

    // Else, do normal capture logic:
    if (toPiece.type === PieceType.Snake && !toPiece.hasShed) {
      this.snakeLivesMatter(toPiece, toPosition, sideEffects);
    } else {
      sideEffects.push(
        new SideEffect(
          crypto.randomUUID() as UUID,
          toPosition,
          [toPiece],
          Effect.Capture()
        )
      );
    }

    // CRITICAL: Clear swing return block for monkey capture moves during bonus turn
    if (piece.type === PieceType.Monkey && this.gameStateManager.getGameState().activeBonusTurnPieceId) {
      this.gameStateManager.clearSwingReturnBlock();
    }

    // Now move the piece (non‐Monkey or no special dice logic)
    this.regularMove(fromPosition, toPosition, piece);
  }

  snakeLivesMatter(
    snakePiece: IPiece,
    capturePosition: Position,
    sideEffects: ISideEffect[]
  ) {
    // Mark the snake as having shed its skin
    snakePiece.hasShed = true;

    // Clear all status effects when snake sheds skin
    snakePiece.isEntangled = false; // Clear entanglement
    snakePiece.entangledPieceId = undefined; // Remove reference to entangled piece
    snakePiece.poisonedStatus = {
      // Clear poison
      isPoisoned: false,
      removeOnTurn: undefined,
    };
    snakePiece.isInfected = false; // Clear infection
    snakePiece.zombifiedStatus = {
      // Clear zombification
      isZombified: false,
      removeOnTurn: undefined,
    };
    snakePiece.isZombified = false;

    // Determine the respawn position
    const respawnRow = snakePiece.isLight ? 0 : 7;
    const respawnPosition = { row: respawnRow, col: capturePosition.col };

    // Check if the respawn position is occupied
    const pieces = this.gameStateManager.getPiecesAt(respawnPosition);
    if (
      pieces.length === 0 ||
      (pieces.length === 1 &&
        pieces.some((piece) => piece.type === PieceType.Tree))
    ) {
      // move the snake to the respawn spot
      this.regularMove(capturePosition, respawnPosition, snakePiece);
    } else {
      // Respawn blocked — complete the original capture
      sideEffects.push(
        new SideEffect(
          crypto.randomUUID() as UUID,
          capturePosition,
          [snakePiece],
          Effect.Capture()
        )
      );
    }
  }

  houseMove(
    fromPosition: Position,
    toPosition: Position,
    piece: IPiece,
    toPiece: IPiece
  ) {
    // CRITICAL: Clear swing return block for monkey house moves during bonus turn
    if (piece.type === PieceType.Monkey && this.gameStateManager.getGameState().activeBonusTurnPieceId) {
      this.gameStateManager.clearSwingReturnBlock();
    }

    this.regularMove(fromPosition, toPosition, piece);
    piece.isHousing = true;
  }

  /**
   * Check if a piece is standing on a fainted goat
   * @param position The position to check
   * @returns True if there's a piece standing on a fainted goat at this position
   */
  isPieceOnFaintedGoat(position: Position): boolean {
    const square = this.gameStateManager.getSquare(position);

    // Check if the square has exactly 2 pieces, one of which is a fainted goat
    return (
      square.Pieces.length === 2 &&
      square.Pieces.some((p) => p.type === PieceType.Goat && p.isFainted)
    );
  }

  /**
   * Get the non-goat piece at a position where a piece is standing on a fainted goat
   * @param position The position to check
   * @returns The non-goat piece, or null if not found
   */
  getPieceOnFaintedGoat(position: Position): IPiece | null {
    const square = this.gameStateManager.getSquare(position);

    // Find the non-fainted goat piece
    return (
      square.Pieces.find((p) => !(p.type === PieceType.Goat && p.isFainted)) ||
      null
    );
  }

  getValidMoves(position: Position, piece: IPiece): IMove[] {
    const currentGameState = this.gameStateManager.getGameState();
    
    // Tutorial mode: Check turn limit enforcement
    if (currentGameState.mode === 'Tutorial') {
      const tutorialTurnLimit = (currentGameState as any).tutorialTurnLimit;
      const tutorialConfig = (currentGameState as any).tutorialConfig;
      const isSoloMode = tutorialConfig?.soloMode || false;
      
      
      if (tutorialTurnLimit && currentGameState.turnCount >= tutorialTurnLimit) {
        return []; // No valid moves when turn limit is reached
      }
    }

    // At the start of a move, clear poison and zombification if their timer is up
    if (piece.poisonedStatus.isPoisoned) {
      // The piece is poisoned and cannot move
      return [];
    }
    if (piece.type === PieceType.Tree && piece.isMeadowed) {
      // The Tree is meadowed and cannot move
      return [];
    }
    if (piece.isEntangled) {
      // The animal is entangled and cannot move
      return [];
    }

    // Special check for fainted goats with another piece on top
    if (piece.type === PieceType.Goat && piece.isFainted) {
      const square = this.gameStateManager.getSquare(position);
      if (square && square.Pieces.length > 1) {
        // If the goat is fainted and shares its square with any other piece, it cannot move
        return [];
      }
    }

    // === Bonus Turn Restriction ===
    const gameState = this.gameStateManager.getGameState();
    if (gameState.activeBonusTurnPieceId) {
      if (piece.id !== gameState.activeBonusTurnPieceId) {
        return [];
      }
    }

    const { row, col } = position;
    const height = this.gameStateManager.getBoardState().length;
    const width = this.gameStateManager.getBoardState()[0]?.length || 0;

    // Check if the position is within the board bounds
    if (row < 0 || row >= height || col < 0 || col < 0 || col >= width) {
      throw new Error("Position is out of bounds");
    }

    // Get the square at the specified position
    const square = this.gameStateManager.getSquare(position);
    if (!square) {
      throw new Error("Square is undefined");
    }

    if (!piece) {
      return [];
    }

    // Get the valid moves for the piece
    const isLightTurn = this.gameStateManager.getGameState().isLightTurn;
    let validMoves = piece.getValidMovesForPiece(
      this.gameStateManager.getBoardState(),
      position,
      isLightTurn,
      this.gameStateManager
    );

    // If bonus turn is active, restrict to valid bonus moves for the piece
    if (gameState.activeBonusTurnPieceId) {
      if (piece.type === PieceType.Virus) {
        validMoves = validMoves.filter(
          (m) =>
            m.moveType === MoveType.infect || m.moveType === MoveType.endTurn
        );
      } else if (piece.type === PieceType.Monkey) {
        // MONKEY FIX: Allow all moves during bonus turns - monkeys should have full movement capabilities
        // No filtering needed - monkeys get all their normal moves plus swing-back option
      } else if (piece.type === PieceType.Penguin) {
        const isZombified = piece.zombifiedStatus?.isZombified || false;

        if (isZombified) {
          // ZOMBIFIED PENGUIN: During bonus turn, allow slide/capture/endTurn (NOT rotation)
          validMoves = validMoves.filter(
            (m) =>
              m.moveType === MoveType.regular || // Slide moves
              m.moveType === MoveType.capture || // Capture moves
              m.moveType === MoveType.endTurn
          );
        } else {
          // REGULAR PENGUIN: During bonus turn, allow rotate/endTurn only
          validMoves = validMoves.filter(
            (m) =>
              m.moveType === MoveType.rotate || // Rotation moves
              (m.moveType === MoveType.noMove && // Old-style rotation moves (noMove with rotate side effects)
                m.sideEffects?.some(
                  (se) => se.effect.type === EffectType.rotate
                )) ||
              m.moveType === MoveType.endTurn
          );
        }
      } // Add more as needed for other bonus turn types
    }

    return validMoves;
  }

  isStalemate(): boolean {
    // Implement stalemate logic if necessary
    return false; // Placeholder implementation
  }

  switchTurn(): void {
    // Implement turn switching logic if necessary
  }

  undoMove(): void {
    // Implement undo move logic if necessary
  }

  handleEntangleSideEffects(
    pieceDoingEntangling: Piece,
    sideEffects: SideEffect[]
  ) {
    const entangleSideEffects = sideEffects.filter(
      (x) => x.effect.type === EffectType.entangle
    );

    if (entangleSideEffects.length === 0) {
      // If there are no entangle side effects and this is an octopus with an entangled piece,
      // we should clear the previous entanglement
      if (pieceDoingEntangling.entangledPieceId) {
        const previouslyEntangledPiece = this.gameStateManager.findPieceById(
          pieceDoingEntangling.entangledPieceId
        );
        if (previouslyEntangledPiece) {
          previouslyEntangledPiece.isEntangled = false;
          pieceDoingEntangling.entangledPieceId = undefined;
        }
      }
      return;
    }

    if (entangleSideEffects.length > 1)
      throw new Error("Multiple entangles in a single move is invalid");

    var entangle = entangleSideEffects[0];

    if (entangle.piecesToEffect.length > 1)
      throw new Error("Only one piece can be entangled per move");

    if (entangle.piecesToEffect.length === 0)
      throw new Error("No piece to entangle");

    // Always release any previously entangled piece before setting a new entanglement
    if (pieceDoingEntangling.entangledPieceId) {
      const previouslyEntangledPiece = this.gameStateManager.findPieceById(
        pieceDoingEntangling.entangledPieceId
      );
      if (previouslyEntangledPiece) {
        previouslyEntangledPiece.isEntangled = false;
      }
      // Clear the reference to the previously entangled piece
      pieceDoingEntangling.entangledPieceId = undefined;
    }

    var pieceToEntangle = entangle.piecesToEffect[0];
    pieceToEntangle.isEntangled = true;
    pieceDoingEntangling.entangledPieceId = pieceToEntangle.id;
  }

  endTurnMove(
    fromPosition: Position,
    toPosition: Position,
    piece: IPiece
  ): void {
    // Implement end turn move logic if necessary
  }

  inkMove(fromPosition: Position, toPosition: Position, piece: IPiece) {
    piece.isShadowed = true;
    this.regularMove(fromPosition, toPosition, piece);
  }

  // Call this at the end of the turn to finalize the FEN history for the turn
  getPendingTurnFENs(): string[] { return [...this.currentTurnFENs]; }

  // Restore pending bonus-move history in an isolated search manager.
  restorePendingTurnFENs(fens: string[]): void { this.currentTurnFENs = [...fens]; }

  finalizeTurnFENHistory() {
    const gameState = this.gameStateManager.getGameState();

    // Phase 4 - SOC Refactoring: Use FENHistoryManager if available
    if (this.fenHistoryManager) {
      this.fenHistoryManager.addFENs(gameState, this.currentTurnFENs);
      this.currentTurnFENs = [];
    } else {
      // Fallback for backward compatibility (if manager not injected)
      // CRITICAL FIX: APPEND to fenHistory instead of replacing it
      // This preserves all previous moves including bonus turn moves
      const addedCount = this.currentTurnFENs.length;

      // Surgical fix for duplicate FENs: De-dupe consecutive identical FENs when appending to history
      // This prevents false threefold repetitions from rapid state updates during monkey swings
      let newHistory = [...gameState.fenHistory];
      this.currentTurnFENs.forEach((fen) => {
        if (newHistory.length === 0 || fen !== newHistory[newHistory.length - 1]) {
          newHistory.push(fen);
        } else {
        }
      });

      // CRITICAL: Create a NEW array instead of mutating with .push()
      // This ensures React detects the change and updates the UI
      gameState.fenHistory = newHistory;
      this.currentTurnFENs = [];
    }
  }

  /**
   * Checks if there are zombie ink shadows on the board that the current player must deal with.
   * Zombie ink shadows persist until the original owner (target color) makes a move.
   */
  public hasZombieInkShadowsForCurrentTurn(): boolean {
    const board = this.gameStateManager.getBoardState();
    const isLightTurn = this.gameStateManager.getGameState().isLightTurn;

    for (const row of board) {
      for (const square of row) {
        for (const piece of square.Pieces) {
          if (piece.isShadow && piece.isZombieInkShadow &&
              piece.zombieInkTargetColor === isLightTurn) {
            return true;
          }
        }
      }
    }
    return false;
  }

  /**
   * Handles the case when a player attempts to move a zombie ink shadow.
   * Returns true if a shadow was selected (trap triggered), false otherwise.
   * When triggered, clears shadows and ends the turn.
   */
  public attemptMoveZombieInkShadow(piece: IPiece, position: Position): boolean {
    if (piece.isShadow && piece.isZombieInkShadow) {

      // Clear all shadows
      this.removeAllShadowPieces();

      // Force turn change via TurnController
      if (this.turnController) {
        this.turnController.forceEndTurn();
      } else {
      }

      return true; // Indicates zombie ink trap triggered
    }
    return false;
  }

  /**
   * Removes all shadow pieces (isShadow === true) from the board.
   * This enforces the one-turn diversion mechanic for shadow octopi.
   */
  private removeAllShadowPieces(): void {
    const board = this.gameStateManager.getBoardState();
    for (let row = 0; row < board.length; row++) {
      for (let col = 0; col < board[row].length; col++) {
        const square = board[row][col];
        // Remove shadow pieces (including zombie ink shadows)
        square.Pieces = square.Pieces.filter((piece) => !piece.isShadow);
        // Remove isShadowed from real octopus pieces
        square.Pieces.forEach((piece) => {
          if (piece.type === PieceType.Octopus && piece.isShadowed) {
            piece.isShadowed = false;
          }
        });
      }
    }
  }

  /**
   * Validates meadow effects on all Trees.
   * Sets isMeadowed based on whether an enemy Fungi is on the same square or adjacent.
   */
  public validateMeadowEffects(): void {
    const board = this.gameStateManager.getBoardState();
    for (let row = 0; row < board.length; row++) {
      for (let col = 0; col < board[row].length; col++) {
        const square = board[row][col];
        const tree = square.Pieces.find((p) => p.type === PieceType.Tree);

        if (tree) {
          // Check if there's an enemy Fungi on the same square
          const hasSameSquareEnemyFungi = square.Pieces.some(
            (p) => p.type === PieceType.Fungi && p.isLight !== tree.isLight
          );

          // Check if there's an adjacent enemy Fungi
          const hasAdjacentEnemyFungi = this.hasAdjacentEnemyFungi(
            { row, col },
            tree.isLight
          );

          // Set meadow status based on enemy fungi presence
          tree.isMeadowed = hasSameSquareEnemyFungi || hasAdjacentEnemyFungi;
        }
      }
    }
  }

  /**
   * Checks if there's an enemy Fungi adjacent to the given position.
   */
  private hasAdjacentEnemyFungi(position: Position, treeIsLight: boolean): boolean {
    const board = this.gameStateManager.getBoardState();
    const adjacentOffsets = [
      [-1, -1], [0, -1], [1, -1],
      [-1, 0],          [1, 0],
      [-1, 1],  [0, 1], [1, 1],
    ];

    for (const [dRow, dCol] of adjacentOffsets) {
      const adjRow = position.row + dRow;
      const adjCol = position.col + dCol;

      // Check bounds
      if (adjRow < 0 || adjRow >= board.length || adjCol < 0 || adjCol >= board[0].length) {
        continue;
      }

      const adjSquare = board[adjRow][adjCol];
      const enemyFungi = adjSquare.Pieces.find(
        (p) => p.type === PieceType.Fungi && p.isLight !== treeIsLight
      );

      if (enemyFungi) {
        return true;
      }
    }

    return false;
  }
}
